import React, { useEffect, useRef, useState } from "react";
import { ResponsiveFunnel } from "@nivo/funnel";
import { ThemeProvider, SvgWrapper } from "@nivo/core";
import { BoxLegendSvg } from "@nivo/legends";
import { FunnelContainer, TooltipBox } from "./FunnelChart.styled";
import { translateCandidate } from "./FunnelChart.utils";

/**
 * Renders a funnel chart to visualize status progression
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Array<{label: string, value: number}>} props.arrayData -
 *  Array of stage objects with:
 *    - `label`: Name of the stage (string)
 *    - `value`: Number of items at this stage (number)
 * @param {Function} props.transformFunc - Data transformation function
 * @param {Object} [props.styling] - Optional styling configuration for the chart
 * @param {string} [props.title] - Optional title for the chart
 *
 * @example
 * const arrayData = [
 *     { label: 'Beworben', value: 100 },
 *     { label: 'Abgesagt', value: 50 },
 *     { label: 'Warten auf Rückmeldung', value: 15 },
 *     { label: 'Zur Vorstellung', value: 10 },
 *     { label: 'Kontaktiert', value: 5 },
 *     { label: 'Automatisch abgesagt', value: 10 }
 *   ]
 *
 * @returns {React.ReactElement} Funnel chart visualization
 */
export const FunnelChart = ({
  arrayData,
  transformFunc,
  styling,
  title,
  defaultSizes = { height: 300, width: 600 },
  t
}) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState(defaultSizes);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    const updateDimensions = () => {
      const { height, width } = container.getBoundingClientRect();
      setDimensions({
        height: Math.floor(height),
        width: Math.floor(width)
      });
    };

    window.addEventListener("resize", updateDimensions);
    updateDimensions();

    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  if (!arrayData) return <></>;

  const chartData = transformFunc(arrayData || [], t);

  const funnelWidth = Math.floor(dimensions.width * 0.6);
  const legendWidth = Math.floor(dimensions.width * 0.3);

  const totalApplicants = chartData[0]?.value || 1;

  const ConversionRateTooltip = ({ part }) => {
    const currentIndex = chartData.findIndex((item) => item.id === part.data.id);
    const conversionRate = ((part.data?.value / totalApplicants) * 100).toFixed(2);

    return (
      <TooltipBox>
        <strong>{part.data?.label}</strong>
        <span>
          {part.data?.value} {translateCandidate(t, part.data?.value)}
        </span>
        {currentIndex > 0 && (
          <span>
            {t ? t("Conversion Rate") : "Konversionsrate"}: {conversionRate}%
          </span>
        )}
      </TooltipBox>
    );
  };

  return (
    <ThemeProvider>
      <FunnelContainer ref={containerRef} styling={styling}>
        <ResponsiveFunnel
          data={chartData}
          height={dimensions.height}
          margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
          width={funnelWidth}
          borderWidth={20}
          beforeSeparatorLength={100}
          beforeSeparatorOffset={20}
          afterSeparatorLength={100}
          afterSeparatorOffset={20}
          currentPartSizeExtension={10}
          currentBorderWidth={40}
          direction="horizontal"
          labelColor="#ffffff"
          layers={["separators", "parts", "labels", "annotations"]}
          colors={chartData.map(({ color }) => color)}
          tooltip={ConversionRateTooltip}
        />
        {title && <p>{title}</p>}
        <SvgWrapper
          height={dimensions.height}
          width={legendWidth}
          margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
        >
          <BoxLegendSvg
            data={chartData}
            anchor="left"
            containerHeight={dimensions.height}
            direction="column"
            // item specific
            itemWidth={200}
            itemHeight={30}
            symbolShape="circle"
          />
        </SvgWrapper>
      </FunnelContainer>
    </ThemeProvider>
  );
};
